import { type ReactElement } from 'react';

import { WhatsAppInboxDocumentCard } from '@/features/notification/components/whatsapp-inbox-document-card.js';
import {
  formatWhatsAppInboxFileSize,
  presentWhatsAppInboxDocument,
} from '@/features/notification/lib/whatsapp-inbox-document.js';
import {
  ChatAttachPreview,
  type ChatAttachPreviewItem,
} from '@/shared/chat/chat-attach-preview.js';
import { ChatAttachVideoPreview, ChatAttachVideoThumb } from '@/shared/chat/chat-attach-video.js';

const ADD_ACCEPT =
  'image/*,video/*,.mp4,.mov,.webm,.mkv,.3gp,application/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip';

export interface WhatsAppInboxPendingAttachment {
  readonly kind: 'image' | 'video' | 'document';
  readonly previewUrl: string;
  readonly fileName: string;
  readonly fileSize: number;
  readonly contentType: string;
}

export interface WhatsAppInboxAttachConfirmProps {
  readonly items: readonly WhatsAppInboxPendingAttachment[];
  readonly selectedIndex: number;
  readonly uploading: boolean;
  readonly onSelect: (index: number) => void;
  readonly onCancel: () => void;
  readonly onConfirm: (captions: readonly (string | undefined)[]) => void;
  readonly onAddFiles?: (files: readonly File[]) => void;
}

function attachmentPreview(item: WhatsAppInboxPendingAttachment, featured: boolean): ReactElement {
  const document = presentWhatsAppInboxDocument(item.fileName, item.contentType);
  const pdfSrc = `${item.previewUrl}#toolbar=0&navpanes=0`;
  if (item.kind === 'document' && document.isPdf) {
    return featured ? (
      <iframe className="chat-attach__pdf" title={item.fileName} src={pdfSrc} />
    ) : (
      <iframe className="chat-attach__thumb-pdf" title="" src={pdfSrc} tabIndex={-1} />
    );
  }
  if (item.kind === 'video') {
    return featured ? (
      <ChatAttachVideoPreview src={item.previewUrl} />
    ) : (
      <ChatAttachVideoThumb src={item.previewUrl} />
    );
  }
  if (item.kind === 'document') {
    return (
      <WhatsAppInboxDocumentCard
        filename={item.fileName}
        mimetype={item.contentType}
        href={null}
        detail={formatWhatsAppInboxFileSize(item.fileSize)}
      />
    );
  }
  return <img src={item.previewUrl} alt={featured ? item.fileName : ''} />;
}

export function WhatsAppInboxAttachConfirm({
  items,
  selectedIndex,
  uploading,
  onSelect,
  onCancel,
  onConfirm,
  onAddFiles,
}: WhatsAppInboxAttachConfirmProps): ReactElement {
  const selected = items[selectedIndex] ?? items[0];
  const dialogLabel =
    selected?.kind === 'document'
      ? 'Confirmar envio do documento'
      : selected?.kind === 'video'
        ? 'Confirmar envio do vídeo'
        : 'Confirmar envio da imagem';
  const previewItems: ChatAttachPreviewItem[] = items.map((item) => ({
    key: item.previewUrl,
    preview: attachmentPreview(item, true),
    thumbnail: attachmentPreview(item, false),
  }));

  return (
    <ChatAttachPreview
      dialogLabel={dialogLabel}
      items={previewItems}
      selectedIndex={selectedIndex}
      uploading={uploading}
      addAccept={ADD_ACCEPT}
      onSelect={onSelect}
      {...(onAddFiles !== undefined ? { onAddFiles } : {})}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}
